import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const InvoiceActionModal = ({ isOpen, onClose, invoice, actionType }) => {
  if (!isOpen) return null;

  const getActionContent = () => {
    switch (actionType) {
      case 'download':
        return {
          icon: 'Download',
          title: 'Download Invoice',
          message: `Invoice ${invoice?.invoiceNumber} PDF is ready for download.`,
          color: 'blue',
          actions: (
            <>
              <Button 
                variant="default" 
                size="sm" 
                iconName="Download" 
                iconPosition="left"
                onClick={() => {
                  console.log('Downloading invoice PDF:', invoice?.invoiceNumber);
                  onClose();
                }}
                className="w-fit min-w-[140px]"
              >
                Download PDF
              </Button>
              <Button 
                variant="outline" 
                size="sm"
                onClick={onClose}
                className="w-fit min-w-[100px]"
              >
                Cancel
              </Button>
            </>
          )
        };
      case 'einvoice':
        return {
          icon: 'FileText',
          title: 'View e-Invoice',
          message: `Access your GST-compliant e-invoice for booking ${invoice?.bookingId}.`,
          color: 'purple',
          actions: (
            <>
              <Button 
                variant="default" 
                size="sm" 
                iconName="ExternalLink" 
                iconPosition="left"
                onClick={() => {
                  console.log('Opening e-invoice:', invoice?.invoiceNumber);
                  onClose();
                }}
                className="w-fit min-w-[140px]"
              >
                Open e-Invoice
              </Button>
              <Button 
                variant="outline" 
                size="sm"
                onClick={onClose}
                className="w-fit min-w-[100px]"
              >
                Cancel
              </Button>
            </>
          )
        };
      case 'eway':
        return {
          icon: 'Truck',
          title: 'Generate e-Way Bill',
          message: `Generate e-Way Bill for shipment to ${invoice?.mill}.`,
          color: 'orange',
          actions: (
            <>
              <Button 
                variant="default" 
                size="sm" 
                iconName="FileText" 
                iconPosition="left"
                onClick={() => {
                  console.log('Generating e-Way Bill:', invoice?.invoiceNumber);
                  onClose();
                }}
                className="w-fit min-w-[140px]"
              >
                Generate Bill
              </Button>
              <Button 
                variant="outline" 
                size="sm"
                onClick={onClose}
                className="w-fit min-w-[100px]"
              >
                Cancel
              </Button>
            </>
          )
        };
      case 'print':
        return {
          icon: 'Printer',
          title: 'Print Invoice',
          message: `Print invoice ${invoice?.invoiceNumber} for your records.`,
          color: 'gray',
          actions: (
            <>
              <Button 
                variant="default" 
                size="sm" 
                iconName="Printer" 
                iconPosition="left"
                onClick={() => {
                  console.log('Printing invoice:', invoice?.invoiceNumber);
                  window?.print();
                  onClose();
                }}
                className="w-fit min-w-[140px]"
              >
                Print Now
              </Button>
              <Button 
                variant="outline" 
                size="sm"
                onClick={onClose}
                className="w-fit min-w-[100px]"
              >
                Cancel
              </Button>
            </>
          )
        };
      case 'support':
        return {
          icon: 'Mail',
          title: 'Contact Support',
          message: 'Our support team is ready to help with your invoice queries.',
          color: 'green',
          actions: (
            <>
              <Button 
                variant="default" 
                size="sm" 
                iconName="Mail" 
                iconPosition="left"
                onClick={() => {
                  console.log('Opening support for invoice:', invoice?.invoiceNumber);
                  onClose();
                }}
                className="w-fit min-w-[140px]"
              >
                Send Message
              </Button>
              <Button 
                variant="secondary" 
                size="sm" 
                iconName="Phone" 
                iconPosition="left"
                onClick={() => {
                  console.log('Calling support');
                  onClose();
                }}
                className="w-fit min-w-[140px]"
              >
                Call Support
              </Button>
              <Button 
                variant="outline" 
                size="sm"
                onClick={onClose}
                className="w-fit min-w-[100px]"
              >
                Cancel
              </Button>
            </>
          )
        };
      default:
        return {
          icon: 'Info',
          title: 'Action',
          message: 'Processing your request...',
          color: 'blue',
          actions: (
            <Button 
              variant="outline" 
              size="sm"
              onClick={onClose}
              className="w-fit min-w-[100px]"
            >
              Close
            </Button>
          )
        };
    }
  };

  const content = getActionContent();
  const colorClasses = {
    blue: 'from-blue-500 to-blue-600',
    purple: 'from-purple-500 to-purple-600',
    orange: 'from-orange-500 to-orange-600',
    gray: 'from-gray-500 to-gray-600',
    green: 'from-green-500 to-green-600'
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4 animate-fadeIn">
      <div className="bg-card rounded-2xl shadow-2xl max-w-md w-full animate-slideUp border border-border">
        <div className={`bg-gradient-to-r ${colorClasses?.[content?.color]} px-6 py-4 rounded-t-2xl`}>
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-full bg-white/20 flex items-center justify-center">
              <Icon name={content?.icon} size={24} color="#FFFFFF" />
            </div>
            <h2 className="text-xl font-bold text-white">{content?.title}</h2>
          </div>
        </div>

        <div className="p-6">
          <div className="mb-6">
            <p className="text-foreground text-base mb-4">{content?.message}</p>
            {invoice && (
              <div className="bg-muted rounded-lg p-4 space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Invoice No:</span>
                  <span className="font-medium text-foreground">{invoice?.invoiceNumber}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Amount:</span>
                  <span className="font-medium text-foreground">₹{invoice?.totalAmount?.toLocaleString('en-IN')}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Date:</span>
                  <span className="font-medium text-foreground">{new Date(invoice?.date)?.toLocaleDateString('en-IN')}</span>
                </div>
              </div>
            )}
          </div>

          <div className="flex flex-wrap gap-3 justify-end">
            {content?.actions}
          </div>
        </div>
      </div>
    </div>
  );
};

export default InvoiceActionModal;